import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQPage = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/faqs`);
      // Filter only visible FAQs and sort by order
      const visibleFaqs = (response.data || [])
        .filter(faq => faq.visible)
        .sort((a, b) => a.order - b.order);
      setFaqs(visibleFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-burgundy text-cream py-4 px-6 shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <a href="/" className="cursor-pointer">
              <img src="/assets/luminelle-logo.png" alt="Luminelle" className="h-10 hover:opacity-80 transition-opacity" />
            </a>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-cream hover:bg-cream/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-burgundy font-serif mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-burgundy/70">
            Find answers to common questions about Luminelle
          </p>
        </div>

        {/* FAQs */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
            <span className="ml-2 text-burgundy">Loading FAQs...</span>
          </div>
        ) : Object.keys(groupedFaqs).length === 0 ? (
          <div className="text-center py-12">
            <p className="text-burgundy/50">No FAQs available at this time.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
              <div key={category}>
                {/* Category Header */}
                <h2 className="text-2xl font-bold text-burgundy font-serif mb-4 capitalize">
                  {category}
                </h2>

                {/* FAQ Items */}
                <div className="space-y-3">
                  {categoryFaqs.map((faq) => (
                    <Card 
                      key={faq.id}
                      className="border-burgundy/20 hover:border-burgundy/40 transition-all duration-200"
                    >
                      <button
                        onClick={() => toggleFAQ(faq.id)}
                        className="w-full text-left p-4 flex items-start justify-between"
                      >
                        <h3 className="text-lg font-medium text-burgundy pr-4">
                          {faq.question}
                        </h3>
                        {expandedId === faq.id ? (
                          <ChevronUp className="h-5 w-5 text-burgundy flex-shrink-0 mt-1" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-burgundy flex-shrink-0 mt-1" />
                        )}
                      </button>

                      {expandedId === faq.id && (
                        <CardContent className="pt-0 pb-4">
                          <div 
                            className="text-burgundy/70 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQPage;
